import React, { useEffect, useRef, useState } from 'react';
import { Document, pdf } from '@react-pdf/renderer';
import { CoverPage, TableOfContentsPage, AboutPage } from '../../components/pdf/2024/sections/CoverAndAboutPDF';
import { EmpoweringPeopleSection } from '../../components/pdf/2024/sections/EmpoweringPeoplePDF';
import { SustainingPlacesSection } from '../../components/pdf/2024/sections/SustainingPlacesPDF';
import { ResponsiblePracticesSection } from '../../components/pdf/2024/sections/ResponsiblePracticesPDF';
import { AppendicesSection } from '../../components/pdf/2024/sections/AppendicesPDF';

/**
 * PDF preview page.
 *
 * We avoid `<PDFViewer>`: under React 18+ StrictMode (enabled in src/index.js)
 * its internal blob URL is revoked by the first cleanup before the iframe
 * finishes loading, leaving the viewer blank.
 *
 * StrictMode also fires this effect twice. We cache the in-flight blob URL
 * on a ref so the second mount reuses the first mount's work, and we never
 * cancel the promise (the first synthetic cleanup would otherwise discard
 * the only result and leave the page stuck on the loading message).
 */
export default function PDFPreview() {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  const stateRef = useRef({ promise: null, url: null });

  useEffect(() => {
    const cache = stateRef.current;

    if (!cache.promise) {
      cache.promise = pdf(
        <Document>
          <CoverPage />
          <TableOfContentsPage /> 
          <AboutPage /> 
          <EmpoweringPeopleSection /> 
          <SustainingPlacesSection />
          <ResponsiblePracticesSection />
          <AppendicesSection />
        </Document>
      )
        .toBlob()
        .then((blob) => {
          const objectUrl = URL.createObjectURL(blob);
          cache.url = objectUrl;
          return objectUrl;
        });
    }

    cache.promise.then(setUrl).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[PDFPreview] toBlob() failed:', err);
      setError(err);
    });

    return () => {
      // Defer revoke so the iframe can finish reading the blob even if a
      // StrictMode/route cleanup fires while it's still streaming.
      const toRevoke = cache.url;
      if (toRevoke) {
        setTimeout(() => URL.revokeObjectURL(toRevoke), 60000);
      }
    };
  }, []);

  if (error) {
    return (
      <div style={{ padding: 16, fontFamily: 'sans-serif' }}>
        <strong>Failed to render PDF:</strong> {error.message}
      </div>
    );
  }

  if (!url) {
    return (
      <div style={{ padding: 16, fontFamily: 'sans-serif' }}>
        Generating PDF…
      </div>
    );
  }

  return (
    <iframe
      title="PDF Preview"
      src={url}
      style={{ width: '100%', height: '100vh', border: 0 }}
    />
  );
}